import { BroadcastChannel } from "broadcast-channel";

// Local reimplementation of @tanstack/query-broadcast-client-experimental's
// broadcastQueryClient. Transport behavior is identical to the upstream plugin;
// the only addition is the meta.disableBroadcast opt-out so a query's cache
// value can be kept isolated per tab (e.g. single-use Sigma embed JWTs).
export function broadcastQueryClient({
  queryClient,
  broadcastChannel = "tanstack-query",
  options,
}) {
  let transaction = false;
  const tx = (cb) => {
    transaction = true;
    cb();
    transaction = false;
  };

  const channel = new BroadcastChannel(broadcastChannel, {
    webWorkerSupport: false,
    ...options,
  });

  const queryCache = queryClient.getQueryCache();

  const unsubscribe = queryCache.subscribe((queryEvent) => {
    if (transaction) {
      return;
    }

    const { query } = queryEvent;

    // Default false: only queries that explicitly set meta.disableBroadcast are
    // excluded from cross-tab cache sync.
    if (query.meta?.disableBroadcast) {
      return;
    }

    const { queryHash, queryKey, state, observers } = query;

    if (queryEvent.type === "updated" && queryEvent.action.type === "success") {
      channel.postMessage({
        type: "updated",
        queryHash,
        queryKey,
        state,
      });
    }

    if (queryEvent.type === "removed" && observers.length > 0) {
      channel.postMessage({
        type: "removed",
        queryHash,
        queryKey,
      });
    }

    if (queryEvent.type === "added") {
      channel.postMessage({
        type: "added",
        queryHash,
        queryKey,
      });
    }
  });

  channel.onmessage = (action) => {
    if (!action?.type) {
      return;
    }

    tx(() => {
      const { type, queryHash, queryKey, state } = action;

      const query = queryCache.get(queryHash);

      // Incoming messages carry no meta, so send-side filtering is the primary
      // mechanism. This guards the case where this tab already holds the query
      // with disableBroadcast (e.g. a mixed-version tab still broadcasting it).
      if (query?.meta?.disableBroadcast) {
        return;
      }

      if (type === "updated") {
        if (query) {
          query.setState(state);
          return;
        }

        queryCache.build(
          queryClient,
          {
            queryKey,
            queryHash,
          },
          state
        );
      } else if (type === "removed") {
        if (query) {
          queryCache.remove(query);
        }
      } else if (type === "added") {
        if (query) {
          query.setState(state);
          return;
        }
        queryCache.build(
          queryClient,
          {
            queryKey,
            queryHash,
          },
          state
        );
      }
    });
  };

  return () => {
    unsubscribe();
    channel.close();
  };
}
